import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { HorizonTrackingService } from '@services/api/horizon-tracking.service';
import { shouldHideError } from '@services/helpers/server-error-silence';
import { ToasterService } from '@services/util/toaster.service';
import { apiErrorBody } from '@services/api/api-error';
import { SharedModule } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputSwitchModule } from 'primeng/inputswitch';
import { Subscription } from 'rxjs';
import { HorizonShareItemRef, HorizonShareable } from '../../lead-horizon-tracking.model';
import {
  ShareFolder,
  ShareRow,
  ShareSort,
  isFloorplanRow,
  lotFloorplanItems,
  shareSelectionText,
  toDocumentRow,
  toLibraryFileRow,
  toLotRow,
} from './horizon-share-dialog.rows';

const MESSAGE_LIMIT = 1000;
const LOTS_FOLDER = 'lots';
const ALL_FOLDER = 'all';
/** Files in a floor plan folder (Sharing Library category or library folder), always offered so the agent can find them. */
const FLOORPLANS_FOLDER = 'floorplans';
/** Every other file of the project's library: the agent may share it without it being approved first. */
const LIBRARY_FOLDER = 'library';

/**
 * "Share documents with buyer": pick lots, Sharing Library documents and any other file of the project's
 * library, add a note, and share them to the buyer's Horizon account. A lot's floor plan goes with it.
 */
@Component({
  selector: 'app-horizon-share-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, SharedModule, DialogModule, CheckboxModule, InputSwitchModule],
  templateUrl: './horizon-share-dialog.component.html',
  styleUrls: ['./horizon-share-dialog.component.scss'],
})
export class HorizonShareDialogComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() leadId: string | null = null;
  @Input() projectId: string | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  /** Emitted after a successful share, so the parent can reload its list. */
  @Output() shared = new EventEmitter<void>();

  readonly messageLimit = MESSAGE_LIMIT;
  readonly sortOptions: { value: ShareSort; label: string }[] = [
    { value: 'recent', label: 'Recently Added' },
    { value: 'name', label: 'Name A–Z' },
    { value: 'type', label: 'Type' },
  ];

  isLoading = false;
  loadError = false;
  folders: ShareFolder[] = [];
  folder = ALL_FOLDER;
  search = '';
  sort: ShareSort = 'recent';
  view: 'list' | 'grid' = 'list';
  rows: ShareRow[] = [];
  /** Shown only once the API sends a date per item (it has none today). */
  hasDates = false;
  message = '';
  notifyEmail = true;
  isSending = false;

  private allRows: ShareRow[] = [];
  private readonly selected = new Map<string, ShareRow>();
  /**
   * Lots (by row key) whose floor plan is left out. A ticked lot sends its floor plan by default (contract §12.4
   * `lots[].floorplan`); the agent unticks "Include floor plan" on that lot to send the lot alone.
   */
  private readonly excludedFloorplans = new Set<string>();
  private readonly brokenThumbs = new Set<string>();
  private subscription: Subscription;

  constructor(
    private horizon: HorizonTrackingService,
    private toaster: ToasterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.reset();
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  private reset(): void {
    this.selected.clear();
    this.folder = ALL_FOLDER;
    this.search = '';
    this.sort = 'recent';
    this.message = '';
    this.notifyEmail = true;
    this.excludedFloorplans.clear();
  }

  load(): void {
    this.subscription?.unsubscribe();
    this.isLoading = true;
    this.loadError = false;
    this.subscription = this.horizon.getShareable(this.projectId).subscribe({
      next: res => {
        const data: HorizonShareable = {
          lots: res?.data?.lots ?? [],
          categories: res?.data?.categories ?? [],
          documents: res?.data?.documents ?? [],
          library_files: res?.data?.library_files ?? [],
        };
        this.build(data);
        this.isLoading = false;
      },
      error: () => {
        this.loadError = !shouldHideError(null);
        this.isLoading = false;
      },
    });
  }

  private build(data: HorizonShareable): void {
    const documents = data.documents.map(toDocumentRow);
    const lots = data.lots.map(toLotRow);
    // A library file that is already an approved document is listed once, as that document.
    const approvedIds = new Set(data.documents.map(doc => doc.id));
    const libraryFiles = (data.library_files ?? [])
      .filter(file => !file.shareable_item_id || !approvedIds.has(file.shareable_item_id))
      .map(toLibraryFileRow);
    this.allRows = [...documents, ...libraryFiles, ...lots];
    this.hasDates = this.allRows.some(row => !!row.updated);

    // Folders list in the library's own order, not the order the documents happen to arrive in.
    // Floor plan categories are gathered into the one Floorplans folder instead of their own.
    const categories = new Map<string, number>();
    [...documents]
      .filter(row => !isFloorplanRow(row))
      .sort((a, b) => a.categoryOrder - b.categoryOrder)
      .forEach(row => categories.set(row.folder, (categories.get(row.folder) ?? 0) + 1));
    // "All Files" and the Floorplans folder hold the approved Sharing Library documents only; the rest of the
    // project's library is listed on its own, under "Project Library".
    this.folders = [
      { key: ALL_FOLDER, label: 'All Files', count: documents.length },
      ...(lots.length ? [{ key: LOTS_FOLDER, label: 'Properties / Lots', count: lots.length }] : []),
      { key: FLOORPLANS_FOLDER, label: 'Floorplans', count: documents.filter(isFloorplanRow).length },
      ...[...categories].map(([label, count]) => ({ key: `cat:${label}`, label, count })),
      ...(libraryFiles.length ? [{ key: LIBRARY_FOLDER, label: 'Project Library', count: libraryFiles.length }] : []),
    ];
    // With no files at all, open on the lots rather than an empty list.
    if (!documents.length && !libraryFiles.length && lots.length) {
      this.folder = LOTS_FOLDER;
    }
    this.refresh();
  }

  // ---- Browsing ------------------------------------------------------------------------------

  setFolder(key: string): void {
    this.folder = key;
    this.refresh();
  }

  folderIcon(item: ShareFolder): string {
    if (item.key === LOTS_FOLDER) {
      return 'fa-regular fa-building';
    }
    if (item.key === FLOORPLANS_FOLDER) {
      return 'fa-regular fa-map';
    }
    if (item.key === LIBRARY_FOLDER) {
      return 'fa-regular fa-folder-open';
    }
    return /image|photo|gallery/i.test(item.label) ? 'fa-regular fa-image' : 'fa-regular fa-folder';
  }

  get isLotsFolder(): boolean {
    return this.folder === LOTS_FOLDER;
  }

  get emptyText(): string {
    if (this.search.trim()) {
      return 'Nothing matches your search.';
    }
    if (this.folder === FLOORPLANS_FOLDER) {
      return 'No floor plans in the Sharing Library yet. Other floor plan files are under Project Library.';
    }
    if (this.folder === LIBRARY_FOLDER) {
      return 'No other files in the project library.';
    }
    return this.isLotsFolder ? 'No lots found.' : 'No documents found.';
  }

  refresh(): void {
    const term = this.search.trim().toLowerCase();
    const inFolder = (row: ShareRow): boolean => {
      if (this.folder === ALL_FOLDER) {
        return row.type === 'document';
      }
      if (this.folder === LOTS_FOLDER) {
        return row.type === 'lot';
      }
      if (this.folder === LIBRARY_FOLDER) {
        return row.type === 'library_file';
      }
      if (this.folder === FLOORPLANS_FOLDER) {
        return row.type === 'document' && isFloorplanRow(row);
      }
      return row.type === 'document' && `cat:${row.folder}` === this.folder;
    };
    const rows = this.allRows.filter(row => inFolder(row) && (!term || row.name.toLowerCase().includes(term)));
    this.rows = rows.sort((a, b) => this.compare(a, b));
  }

  private compare(a: ShareRow, b: ShareRow): number {
    if (this.sort === 'name') {
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    }
    if (this.sort === 'type') {
      return a.typeLabel.localeCompare(b.typeLabel) || a.name.localeCompare(b.name, undefined, { numeric: true });
    }
    // Newest approval first; rows without a date keep the order the API sent.
    if (a.addedAt && b.addedAt && a.addedAt !== b.addedAt) {
      return b.addedAt.localeCompare(a.addedAt);
    }
    return a.order - b.order;
  }

  thumbnail(row: ShareRow): string | null {
    return row.thumbnail && !this.brokenThumbs.has(row.key) ? row.thumbnail : null;
  }

  markBroken(row: ShareRow): void {
    this.brokenThumbs.add(row.key);
  }

  // ---- Selection -----------------------------------------------------------------------------

  isSelected(row: ShareRow): boolean {
    return this.selected.has(row.key);
  }

  toggle(row: ShareRow): void {
    if (!this.selected.delete(row.key)) {
      this.selected.set(row.key, row);
    }
  }

  get allVisibleSelected(): boolean {
    return !!this.rows.length && this.rows.every(row => this.selected.has(row.key));
  }

  toggleAllVisible(): void {
    const select = !this.allVisibleSelected;
    this.rows.forEach(row => (select ? this.selected.set(row.key, row) : this.selected.delete(row.key)));
  }

  get selectedCount(): number {
    return this.selected.size;
  }

  /** "1 lot · 1 file selected": a lot's floor plan counts as a file. */
  get selectionText(): string {
    return shareSelectionText([...this.selected.values()], this.floorplanItems, plan => this.rowFor(plan)?.sizeBytes ?? null);
  }

  /** A ticked lot with a linked floor plan offers "Include floor plan". */
  showsFloorplanOption(row: ShareRow): boolean {
    return row.type === 'lot' && !!row.floorplan && this.isSelected(row);
  }

  includesFloorplan(row: ShareRow): boolean {
    return !this.excludedFloorplans.has(row.key);
  }

  toggleFloorplan(row: ShareRow): void {
    if (!this.excludedFloorplans.delete(row.key)) {
      this.excludedFloorplans.add(row.key);
    }
  }

  /** The floor plans that go with the selected lots, each once. */
  private get floorplanItems(): HorizonShareItemRef[] {
    return lotFloorplanItems([...this.selected.values()], this.excludedFloorplans);
  }

  /** The dialog's row for a share item (row keys are `type:id`), for its size. */
  private rowFor(item: HorizonShareItemRef): ShareRow | undefined {
    return this.allRows.find(row => row.key === `${item.type}:${item.id}`);
  }

  // ---- Sharing -------------------------------------------------------------------------------

  send(): void {
    if (this.isSending) {
      return;
    }
    if (!this.selectedCount) {
      this.toaster.Error('Choose at least one lot or document to share.');
      return;
    }
    const items: HorizonShareItemRef[] = [...[...this.selected.values()].map(row => ({ type: row.type, id: row.id })), ...this.floorplanItems];
    this.isSending = true;
    this.horizon.createLeadShare(this.leadId, { items, message: this.message.trim() || undefined, notify_email: this.notifyEmail }).subscribe({
      next: res => {
        this.isSending = false;
        this.toaster.Success(
          /^(ok|success)\.?$/i.test(res?.message ?? '') || !res?.message ? 'Shared with the buyer. They have been notified in Horizon.' : res.message
        );
        this.close();
        this.shared.emit();
      },
      error: err => {
        this.isSending = false;
        // `post` hands back the response body, so `422 not_shareable` / `not_invited` arrive as `{ message }`.
        this.toaster.Error(apiErrorBody(err).message || 'The items could not be shared.');
      },
    });
  }

  trackByKey(_index: number, item: { key: string }): string {
    return item.key;
  }
}
